import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import http, { setAuthToken, getAuthToken } from "./api";
import { trackAction } from "./behavior";
import { dict, t as tFn } from "./i18n";
import { applyAdminTheme } from "./lib/theme";

const AppCtx = createContext(null);

export const useApp = () => useContext(AppCtx);

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("rg_lang") || "ar");
  const [currency, setCurrency] = useState(() => {
    // One-time migration: the old buggy default for this Qatar store was "SAR".
    // Payments are charged in QAR, so reset the legacy default to QAR (ر.ق) once
    // so returning visitors also see the correct currency. A later manual pick
    // of SAR still persists (guard flag is already set).
    const saved = localStorage.getItem("rg_currency");
    if (saved === "SAR" && !localStorage.getItem("rg_ccy_migrated")) {
      localStorage.setItem("rg_ccy_migrated", "1");
      return "QAR";
    }
    return saved || "QAR";
  });
  const [currencies, setCurrencies] = useState({ QAR: { symbol: "ر.ق", rate: 1 } });
  const [country, setCountry] = useState(() => localStorage.getItem("rg_country") || "QA");
  const [city, setCity] = useState(() => localStorage.getItem("rg_city") || "");
  const [branchId, setBranchIdState] = useState(() => localStorage.getItem("rg_branch_id") || "");
  const setBranchId = useCallback((id) => setBranchIdState(id || ""), []);
  const [countries, setCountries] = useState([]);
  const [user, setUser] = useState(null);
  // authReady becomes true once we've either:
  //   - resolved the session via /auth/me (cookie present), or
  //   - confirmed there is no session (anonymous visitor).
  // Components like RequireRole rely on this to avoid flashing /login
  // before the user is hydrated on hard refresh.
  //
  // SECURITY (iter#43): The JWT is now stored ONLY in an httpOnly cookie set
  // by the backend at login. We no longer keep it in localStorage (which is
  // readable by any XSS payload). `token` here is just a "do we have a
  // logged-in session" boolean kept for legacy components that still gate UI
  // on `!!token`; the actual JWT lives in the cookie + `withCredentials: true`
  // sends it on every request automatically.
  const [authReady, setAuthReady] = useState(false);
  const [token, setToken] = useState(null);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("rg_cart") || "[]"); } catch { return []; }
  });
  const [categories, setCategories] = useState([]);
  // Favorites — pure client-side set of product_ids, persisted in localStorage.
  // Works for guests; on login we merge (no conflict needed — union of both).
  const [favorites, setFavorites] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("rg_favs") || "[]")); }
    catch { return new Set(); }
  });

  // Apply dir / lang
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    localStorage.setItem("rg_lang", lang);
  }, [lang]);

  // Storefront theme is fixed to the classic light look (ivory + green + gold).
  // Dark mode was removed by user request. (Admin panel keeps its own toggle.)
  useEffect(() => {
    try { localStorage.setItem("rg_theme", "light"); } catch {}
    document.documentElement.setAttribute("data-theme", "light");
  }, []);

  // Admin-configured storefront colors + seasonal logo (Admin → Settings)
  const [customLogo, setCustomLogo] = useState("");
  useEffect(() => {
    http.get("/theme")
      .then(({ data }) => {
        applyAdminTheme(data?.colors);
        setCustomLogo(data?.custom_logo || "");
      })
      .catch(() => {});
  }, []);

  // Paid-ads attribution: capture ?ad=CODE once, remember for checkout, count the click.
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const ad = sp.get("ad");
      if (ad) {
        localStorage.setItem("rg_ad_code", ad);
        if (!sessionStorage.getItem("rg_ad_clicked")) {
          sessionStorage.setItem("rg_ad_clicked", "1");
          http.post("/ads/click", { code: ad }).catch(() => {});
        }
      }
      // UTM attribution (utm_source/campaign/...) — تُربط بالطلب عند الشراء
      const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
      if (keys.some((k) => sp.get(k))) {
        const a = { ts: Date.now() };
        keys.forEach((k) => { const v = sp.get(k); if (v) a[k] = v.slice(0, 120); });
        localStorage.setItem("rg_attrib", JSON.stringify(a));
      }
    } catch {}
  }, []);

  useEffect(() => { localStorage.setItem("rg_currency", currency); }, [currency]);
  useEffect(() => { localStorage.setItem("rg_country", country); }, [country]);
  useEffect(() => { localStorage.setItem("rg_city", city || ""); }, [city]);
  useEffect(() => {
    if (branchId) localStorage.setItem("rg_branch_id", branchId);
    else localStorage.removeItem("rg_branch_id");
  }, [branchId]);
  useEffect(() => {
    // Load active countries once. Fail-safe: keep defaults on network failure.
    http.get("/countries")
      .then(({ data }) => { if (Array.isArray(data) && data.length) setCountries(data); })
      .catch(() => {});
  }, []);
  useEffect(() => { localStorage.setItem("rg_cart", JSON.stringify(cart)); }, [cart]);
  // Iter#112 — abandoned-cart snapshot for logged-in users (backend sends the
  // "complete your order" reminder after 2h). Debounced; guests are skipped.
  useEffect(() => {
    if (!user) return;
    const id = setTimeout(() => {
      const items = cart.map((c) => ({
        product_id: c.product_id,
        name_ar: c._snapshot?.name_ar || c._snapshot?.name_en || "",
        qty: c.quantity || 1,
        price_sar: c._snapshot?.price_sar || 0,
      }));
      http.post("/cart/snapshot", { items }).catch(() => {});
    }, 4000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, user]);
  useEffect(() => { localStorage.setItem("rg_favs", JSON.stringify([...favorites])); }, [favorites]);

  const toggleFavorite = useCallback(async (productId) => {
    let wasFav = false;
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) { next.delete(productId); wasFav = true; }
      else next.add(productId);
      return next;
    });
    // Best-effort server sync for logged-in users (ignore errors so guest UX stays fast).
    if (user) {
      try {
        if (wasFav) await http.delete(`/favorites/${productId}`);
        else await http.post(`/favorites/${productId}`);
      } catch { /* non-fatal */ }
    }
  }, [user]);
  const isFavorite = useCallback((productId) => favorites.has(productId), [favorites]);

  // Load currencies & categories on mount.
  // Iter#52 perf-fix: cap to 200 root-level categories — most layouts only
  // show top-level chips, and admin pages re-fetch their own scoped lists.
  // Saves ~1.2 MB on every cold-load (was returning 2059 raregroup items).
  useEffect(() => {
    (async () => {
      try {
        const _cc = localStorage.getItem("rg_country") || "QA";
        const _cb = localStorage.getItem("rg_branch_id") || "";
        const [{ data: c }, { data: cats }] = await Promise.all([
          http.get("/currencies"),
          http.get(`/categories?limit=200&country=${_cc}${_cb ? `&branch_id=${_cb}` : ""}`),
        ]);
        setCurrencies(c);
        setCategories(cats);
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Sync local favorites to the server whenever we have a user session.
  // Merges client+server sets (server returns the union) so switching devices
  // doesn't lose any hearts.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const localIds = [...favorites];
        const { data } = await http.post("/favorites/sync", { product_ids: localIds });
        const merged = new Set(data.product_ids || []);
        // Only update if the merged set differs to avoid render loops
        if (merged.size !== favorites.size || [...merged].some((x) => !favorites.has(x))) {
          setFavorites(merged);
        }
      } catch { /* non-fatal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Bootstrap session: try /auth/me on mount. If the httpOnly cookie is
  // present (same-site), this returns the user; otherwise it 401s and we
  // stay anonymous. NOTE: the in-memory Bearer token is NOT recovered on
  // hard refresh — that's intentional. UX-wise, on the prod cross-origin
  // setup the cookie call also 401s, so the user gets a clean login prompt.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) return;
    let cancelled = false;
    // ⚡ استعادة فورية من النسخة المخزنة — لا شاشة فارغة أبداً حتى لو كان
    // الخادم يعيد التشغيل (نشر جديد) أو الشبكة بطيئة. تُحدَّث بالخلفية.
    try {
      if (getAuthToken()) {
        const cached = JSON.parse(localStorage.getItem("rg_user_cache") || "null");
        if (cached?.id) {
          setUser(cached);
          setToken("auth");
          setAuthReady(true);
        }
      }
    } catch { /* noop */ }
    const boot = async (attempt = 0) => {
      try {
        const { data } = await http.get("/auth/me", { timeout: 12000 });
        if (cancelled) return;
        setUser(data);
        try { localStorage.setItem("rg_user_cache", JSON.stringify(data)); } catch { /* noop */ }
        setToken("cookie");
        // Persist a Bearer token so the session survives reloads / PWA reopen
        // even when the httpOnly cookie is unavailable (cross-site or iOS
        // WebView). OAuth flows only plant the cookie, so fetch a token once.
        try {
          if (!getAuthToken()) {
            const { data: tk } = await http.get("/auth/token");
            if (tk?.token) setAuthToken(tk.token);
          }
        } catch { /* non-fatal */ }
        // Iter#44 — Apple Sign-In completed via `?apple=ok` flag from the
        // /auth/apple/callback redirect. Strip the flag once the session
        // hydrates so a refresh stays clean.
        if (typeof window !== "undefined" && window.location.search.includes("apple=ok")) {
          const url = new URL(window.location.href);
          url.searchParams.delete("apple");
          window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);
        }
        setAuthReady(true);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 401) {
          // رفض قاطع من الباكند (توكن منتهٍ/غير صالح) — تسجيل خروج نظيف
          setAuthToken(null);
          setUser(null);
          setToken(null);
          try { localStorage.removeItem("rg_user_cache"); } catch { /* noop */ }
          setAuthReady(true);
        } else if (getAuthToken()) {
          // توكن محفوظ + فشل عابر (شبكة/429/5xx أو 403 من WAF/Cloudflare):
          // لا نطرد المستخدم — نعيد المحاولة بالخلفية، لكن بعد محاولتين
          // نعرض الواجهة بالجلسة المخزنة بدل إبقاء الشاشة فارغة.
          if (attempt >= 1) setAuthReady(true);
          const delay = Math.min(1500 * Math.pow(2, attempt), 10000);
          setTimeout(() => { if (!cancelled) boot(attempt + 1); }, delay);
        } else if (attempt < 2) {
          // زائر بلا توكن: محاولتان إضافيتان ثم تصفح مجهول عادي
          setTimeout(() => { if (!cancelled) boot(attempt + 1); }, 1500 * (attempt + 1));
        } else {
          setAuthReady(true);
        }
      }
    };
    boot();
    return () => { cancelled = true; };
  }, []);

  // SECURITY (iter#43+): each login flow stashes the JWT both in:
  //  1) an httpOnly cookie set by the backend (same-site only, XSS-safe), AND
  //  2) an in-memory Bearer token via setAuthToken (cross-origin fallback).
  // Combined, these cover both the (preview/single-domain) and the
  // (production/separate-subdomain) topologies. Crucially the JWT NEVER
  // touches localStorage, so XSS still can't read it.

  const _applyAuth = (data) => {
    setAuthToken(data.token || null);   // primary: in-memory Bearer
    setToken(data.token ? "auth" : null); // sentinel for `!!token` callers
    setUser(data.user);
    try { localStorage.setItem("rg_user_cache", JSON.stringify(data.user)); } catch { /* noop */ }
    return data.user;
  };

  const login = useCallback(async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    return _applyAuth(data);
  }, []);

  // Exchange Emergent session_id (from OAuth redirect hash) for our own JWT cookie.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const emergentSessionLogin = useCallback(async (sessionId) => {
    const { data } = await http.post("/auth/emergent-session", { session_id: sessionId });
    return _applyAuth(data);
  }, []);

  // WhatsApp OTP flow helpers
  const otpSend = useCallback(async (phone) => {
    const { data } = await http.post("/auth/otp/send", { phone });
    return data;
  }, []);
  const otpVerifyLogin = useCallback(async (phone, code, name) => {
    const { data } = await http.post("/auth/otp/verify", { phone, code, name });
    return _applyAuth(data);
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await http.post("/auth/register", payload);
    return _applyAuth(data);
  }, []);

  const logout = useCallback(async () => {
    try { await http.post("/auth/logout"); } catch { /* non-fatal */ }
    setAuthToken(null);
    setToken(null);
    setUser(null);
    setCart([]);
    try { localStorage.removeItem("rg_user_cache"); } catch { /* noop */ }
  }, []);

  const addToCart = useCallback((product, qty = 1, variant = null, selectedOptions = null) => {
    try { trackAction("add_to_cart", { product_id: product.id, product_name: product.name_ar || product.name_en }); } catch {}
    // عزو إضافة السلة لدائرة «هدايا من RG» إن كان الدخول عبرها خلال آخر 30 دقيقة
    try {
      const raw = sessionStorage.getItem("rg_gifts_src");
      if (raw) {
        const a = JSON.parse(raw);
        if (a?.gift_id && Date.now() - (a.ts || 0) < 30 * 60 * 1000) {
          http.post("/home-gifts/track", {
            event: "add_to_cart", gift_id: a.gift_id, source: a.source || "home_rg_gifts",
            product_id: product.id, product_name: product.name_ar || product.name_en || "",
          }).catch(() => {});
        } else {
          sessionStorage.removeItem("rg_gifts_src");
        }
      }
    } catch {}
    setCart((prev) => {
      // Cart key includes a hash of selected_options so identical product+variant
      // with different option selections is a separate cart line (iter#36).
      const optsKey = (so) => {
        if (!so || !so.length) return "";
        return so.map((g) => `${g.group_id}:${(g.option_ids || []).join(",")}`).join("|");
      };
      const key = (i) => `${i.product_id}__${i.variant_id || ""}__${optsKey(i.selected_options)}`;
      const newKey = `${product.id}__${variant?.id || ""}__${optsKey(selectedOptions)}`;
      const existing = prev.find((i) => key(i) === newKey);
      if (existing) {
        return prev.map((i) => key(i) === newKey ? { ...i, quantity: i.quantity + qty } : i);
      }
      // Compute price snapshot — base + variant + options price deltas
      let basePrice = variant ? variant.price_sar : product.price_sar;
      let optionsDelta = 0;
      const optsLabels = [];
      const groupsLookup = {};
      (product.option_groups || []).forEach((g) => { groupsLookup[g.id] = g; });
      (selectedOptions || []).forEach((sel) => {
        const grp = groupsLookup[sel.group_id];
        if (!grp) return;
        (grp.options || []).forEach((opt) => {
          if ((sel.option_ids || []).includes(opt.id)) {
            optionsDelta += Number(opt.price_delta_sar || 0);
            optsLabels.push({
              group_title_ar: grp.title_ar, group_title_en: grp.title_en,
              is_cash: !!grp.is_cash,
              label_ar: opt.label_ar, label_en: opt.label_en,
              price_delta_sar: Number(opt.price_delta_sar || 0),
              image: opt.image || "",
            });
          }
        });
      });
      return [...prev, {
        product_id: product.id,
        variant_id: variant?.id || null,
        custom_color: variant?.custom_color || null,
        selected_options: selectedOptions || null,
        quantity: qty,
        _snapshot: {
          name_ar: product.name_ar, name_en: product.name_en,
          image: (variant && variant.image) ? variant.image : product.image,
          price_sar: Number(basePrice) + optionsDelta,
          base_price_sar: Number(basePrice),
          original_price_sar: Number(product.price_previous || product.original_price_sar || basePrice),
          options_delta_sar: optionsDelta,
          variant_name: variant?.name || null,
          options_labels: optsLabels,
          source: product.source || null,
        },
      }];
    });
  }, []);

  const _matchCartItem = (i, product_id, variant_id, selected_options = null) => {
    const optsKey = (so) => {
      if (!so || !so.length) return "";
      return so.map((g) => `${g.group_id}:${(g.option_ids || []).join(",")}`).join("|");
    };
    return i.product_id === product_id
      && (i.variant_id || null) === (variant_id || null)
      && optsKey(i.selected_options) === optsKey(selected_options);
  };

  const updateQty = useCallback((product_id, qty, variant_id = null, selected_options = null) => {
    setCart((prev) => prev.map((i) => _matchCartItem(i, product_id, variant_id, selected_options) ? { ...i, quantity: Math.max(1, qty) } : i));
  }, []);

  const removeFromCart = useCallback((product_id, variant_id = null, selected_options = null) => {
    setCart((prev) => prev.filter((i) => !_matchCartItem(i, product_id, variant_id, selected_options)));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const formatPrice = useCallback((priceSar) => {
    // 🌍 أسواق بعملة محلية مباشرة: الأسعار مخزنة بالعملة المحلية بعد تجاوز سعر الدولة — بلا تحويل
    const LOCAL = { SA: ["ر.س", "SAR"], AE: ["د.إ", "AED"], KW: ["د.ك", "KWD"], BH: ["د.ب", "BHD"], OM: ["ر.ع", "OMR"], EG: ["ج.م", "EGP"] };
    const loc = country !== "QA" ? LOCAL[country] : null;
    const info = loc
      ? { rate: 1, symbol: loc[0] }
      : (currencies[currency] || { rate: 1, symbol: "" });
    // السعر يظهر بالأرقام الإنجليزية دائماً في اللغتين — أي أرقام عربية مخزنة تُحوَّل
    const raw = typeof priceSar === "string"
      ? priceSar
          .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d))
          .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
      : priceSar;
    const value = (Number(raw) || 0) * info.rate;
    // بالإنجليزية: رمز العملة يظهر بحروف لاتينية (QAR بدل ر.ق) — وبالعربية يبقى كما هو
    const symbol = lang === "ar" ? info.symbol
      : (loc ? loc[1] : (/[\u0600-\u06FF]/.test(info.symbol) ? currency : info.symbol));
    const fixed = value >= 100 ? value.toFixed(0) : value.toFixed(2);
    return lang === "ar" ? `${fixed} ${symbol}` : `${symbol} ${fixed}`;
  }, [currency, currencies, lang, country]);

  const t = useCallback((key) => tFn(lang, key), [lang]);

  const value = {
    lang, setLang,
    currency, setCurrency, currencies,
    country, setCountry, city, setCity, countries,
    branchId, setBranchId,
    user, setUser, authReady, token,
    login, emergentSessionLogin, otpSend, otpVerifyLogin,
    register, logout,
    favorites, toggleFavorite, isFavorite,
    cart, addToCart, updateQty, removeFromCart, clearCart,
    categories,
    customLogo,
    formatPrice, t, dict,
  };

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}
